
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Users, ChevronDown, Bookmark } from 'lucide-react';
import { Recipe } from '@/types';
import NutriscoreBar from '@/components/NutriscoreBar';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useMenu } from '@/contexts/MenuContext';
import { toast } from 'sonner';
import RecipeLikeButton from './RecipeLikeButton';
import { triggerFavoriteAdd, triggerMenuAdd } from '@/utils/onboardingEvents';
import { format, addDays, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';

interface RecipeCardProps {
  recipe: Recipe;
  showAddToMenu?: boolean;
  onAddToMenu?: (recipeId: string, day: string, mealType: string, portionSize: number) => void;
}

const RecipeCard: React.FC<RecipeCardProps> = ({ 
  recipe, 
  showAddToMenu = false,
  onAddToMenu
}) => {
  const { user, toggleFavorite, hasActiveSubscription } = useAuth();
  const { availableMealTypes, addRecipeToMenu } = useMenu();
  
  const [portion, setPortion] = useState<number>(1);
  const [selectedDay, setSelectedDay] = useState<string>('monday');
  const [selectedMeal, setSelectedMeal] = useState<string>('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);

  // Sanitize and validate recipe data to ensure proper display
  const sanitizedRecipe = React.useMemo(() => {
    // Create a deep copy to avoid modifying the original
    const result = { ...recipe };
    
    // Sanitize ingredients - ensure we have valid objects
    result.ingredients = Array.isArray(recipe.ingredients) 
      ? recipe.ingredients
          .filter(ing => 
            ing && 
            typeof ing === 'object' && 
            ing.name && 
            typeof ing.name === 'string' && 
            ing.name.trim() !== '' && 
            ing.name.trim() !== 'Sin nombre' &&
            ing.name.trim() !== 'undefined'
          )
          .map(ing => ({
            ...ing,
            id: ing.id || `ing-${Math.random().toString(36).substr(2, 9)}`,
            name: ing.name ? String(ing.name).trim() : 'Ingrediente',
            amount: typeof ing.amount === 'number' ? ing.amount : parseFloat(String(ing.amount)) || 0,
            unit: ing.unit ? String(ing.unit).trim() : 'unidad'
          }))
      : [];
    
    // If we have no valid ingredients, add a placeholder
    if (result.ingredients.length === 0) {
      result.ingredients = [
        { id: `ing-default-${Date.now()}`, name: 'Ingrediente', amount: 1, unit: 'unidad' }
      ];
    }
    
    // Sanitize instructions - ensure they're proper strings
    result.instructions = Array.isArray(recipe.instructions) 
      ? recipe.instructions
          .filter(instr => instr !== null && instr !== undefined)
          .map(instr => {
            // Ensure it's a string
            let cleanedInstr = String(instr || '').trim();
            
            // Remove JSON artifacts and property names
            cleanedInstr = cleanedInstr
              .replace(/\\"/g, '"')
              .replace(/^"|"$/g, '')
              .replace(/["\\{}\[\]]+/g, '')
              .replace(/(amount|unit|name|id)[\s:]*[,]?/g, '')
              .trim();
              
            return cleanedInstr;
          })
          .filter(instr => instr !== '')
      : [];
    
    // If we have no valid instructions, add placeholders
    if (result.instructions.length === 0) {
      result.instructions = ['Paso 1 de la receta', 'Paso 2 de la receta'];
    }
    
    // Sanitize tags
    result.tags = Array.isArray(recipe.tags) 
      ? recipe.tags
          .filter(tag => tag && typeof tag === 'string' && tag.trim() !== '')
          .map(tag => String(tag).trim())
      : [];
    
    return result;
  }, [recipe]);

  const isFavorite = user?.favorites?.includes(recipe.id) || false;

  useEffect(() => {
    if (availableMealTypes.length > 0 && !selectedMeal) {
      setSelectedMeal(availableMealTypes[0]);
    }
  }, [availableMealTypes, selectedMeal]);

  const handleAddToMenu = async () => {
    if (!hasActiveSubscription) {
      toast.error("Necesitas una suscripción activa para añadir recetas al menú");
      return;
    }

    if (onAddToMenu) {
      onAddToMenu(recipe.id, selectedDay, selectedMeal, portion);
      triggerMenuAdd();
      console.log("Triggered menu-added event from RecipeCard (onAddToMenu)");
      setMenuOpen(false);
      setAddSuccess(true);
      
      setTimeout(() => {
        setAddSuccess(false);
      }, 5000);
      
      return;
    }
    
    setIsAdding(true);
    try {
      await addRecipeToMenu(recipe.id, selectedDay, selectedMeal, portion);
      triggerMenuAdd();
      console.log("Triggered menu-added event from RecipeCard");
      toast.success(`${sanitizedRecipe.title} añadido al menú del ${getDayName(selectedDay)}`);
      setMenuOpen(false);
      setAddSuccess(true);
      
      setTimeout(() => {
        setAddSuccess(false);
      }, 5000);
      
    } catch (error) {
      toast.error('Error al añadir la receta al menú');
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error("Necesitas iniciar sesión para guardar favoritos");
      return;
    }
    
    toggleFavorite(recipe.id);
    
    // Trigger the event only when adding to favorites, not when removing
    if (!isFavorite) {
      triggerFavoriteAdd();
      console.log("Triggered favorite-added event from RecipeCard");
    }
  };

  const getDayName = (day: string) => {
    const days: Record<string, string> = {
      monday: 'Lunes',
      tuesday: 'Martes',
      wednesday: 'Miércoles',
      thursday: 'Jueves',
      friday: 'Viernes',
      saturday: 'Sábado',
      sunday: 'Domingo',
      monday2: 'Lunes (próx.)',
      tuesday2: 'Martes (próx.)',
      wednesday2: 'Miércoles (próx.)',
      thursday2: 'Jueves (próx.)',
      friday2: 'Viernes (próx.)',
      saturday2: 'Sábado (próx.)',
      sunday2: 'Domingo (próx.)',
    };
    return days[day];
  };

  const getMealName = (meal: string) => {
    const meals: Record<string, string> = {
      breakfast: 'Desayuno',
      morning_snack: 'Media mañana',
      lunch: 'Almuerzo',
      afternoon_snack: 'Merienda',
      dinner: 'Cena',
      evening_snack: 'Tentempié nocturno'
    };
    return meals[meal] || meal;
  };

  const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const daysWithDates = [
    { key: 'monday', name: 'Lunes', date: addDays(currentWeekStart, 0) },
    { key: 'tuesday', name: 'Martes', date: addDays(currentWeekStart, 1) },
    { key: 'wednesday', name: 'Miércoles', date: addDays(currentWeekStart, 2) },
    { key: 'thursday', name: 'Jueves', date: addDays(currentWeekStart, 3) },
    { key: 'friday', name: 'Viernes', date: addDays(currentWeekStart, 4) },
    { key: 'saturday', name: 'Sábado', date: addDays(currentWeekStart, 5) },
    { key: 'sunday', name: 'Domingo', date: addDays(currentWeekStart, 6) },
    // Add second week days
    { key: 'monday2', name: 'Lunes (próx.)', date: addDays(currentWeekStart, 7) },
    { key: 'tuesday2', name: 'Martes (próx.)', date: addDays(currentWeekStart, 8) },
    { key: 'wednesday2', name: 'Miércoles (próx.)', date: addDays(currentWeekStart, 9) },
    { key: 'thursday2', name: 'Jueves (próx.)', date: addDays(currentWeekStart, 10) },
    { key: 'friday2', name: 'Viernes (próx.)', date: addDays(currentWeekStart, 11) },
    { key: 'saturday2', name: 'Sábado (próx.)', date: addDays(currentWeekStart, 12) },
    { key: 'sunday2', name: 'Domingo (próx.)', date: addDays(currentWeekStart, 13) }
  ];

  return (
    <Card className="overflow-hidden recipe-card border border-gray-200 h-full flex flex-col">
      <div className="aspect-video w-full overflow-hidden relative">
        <img 
          src={sanitizedRecipe.imageUrl} 
          alt={sanitizedRecipe.title} 
          className="w-full h-full object-cover transition-transform hover:scale-105"
        />
        <div className="absolute top-2 right-2 flex gap-3">
          {user && (
            <div className="z-10">
              <Button 
                variant="ghost" 
                size="icon" 
                className={`rounded-full bg-white/70 backdrop-blur-sm hover:bg-white/90 ${isFavorite ? 'text-blue-600' : 'text-gray-500'}`}
                onClick={handleToggleFavorite}
                title="Guardar en favoritos"
              >
                <Bookmark className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
              </Button>
            </div>
          )}
          <div className="z-10">
            <RecipeLikeButton recipeId={sanitizedRecipe.id} size="sm" />
          </div>
        </div>
      </div>
      <CardHeader className="p-4 pb-2">
        <Link to={`/receta/${sanitizedRecipe.id}`} className="text-lg font-semibold text-vegana-primary hover:text-vegana-accent">
          {sanitizedRecipe.title}
        </Link>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Clock size={16} />
            <span>{sanitizedRecipe.prepTime + sanitizedRecipe.cookTime} min</span>
          </div>
          <div className="flex items-center gap-1">
            <Users size={16} />
            <span>{sanitizedRecipe.servings} pers.</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-2 flex-grow">
        <p className="text-gray-600 text-sm line-clamp-2">{sanitizedRecipe.description}</p>
        {sanitizedRecipe.nutriscore && (
          <div className="mt-3">
            <NutriscoreBar value={sanitizedRecipe.nutriscore} readonly />
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-1">
          {sanitizedRecipe.tags.map((tag, index) => (
            <span 
              key={index} 
              className="text-xs px-2 py-1 bg-vegana-secondary/20 text-vegana-primary rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      </CardContent>
      <CardFooter className="p-4 pt-0">
        <div className="flex flex-col w-full space-y-2">
          <div className="flex justify-between items-center w-full">
            <div className="text-vegana-primary font-semibold">
              {sanitizedRecipe.calories} kcal
            </div>
            {showAddToMenu && (
              <Popover open={menuOpen} onOpenChange={setMenuOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-vegana-primary text-vegana-primary hover:bg-vegana-primary hover:text-white"
                  >
                    Añadir al Menú
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-4" align="end">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="day-select">Día</Label>
                      <Select 
                        value={selectedDay} 
                        onValueChange={setSelectedDay}
                      >
                        <SelectTrigger id="day-select">
                          <SelectValue placeholder="Seleccionar día" />
                        </SelectTrigger>
                        <SelectContent>
                          {daysWithDates.map((day) => (
                            <SelectItem key={day.key} value={day.key}>
                              {day.name} ({format(day.date, "d MMM", { locale: es })})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="meal-select">Comida</Label>
                      <Select 
                        value={selectedMeal} 
                        onValueChange={setSelectedMeal}
                      >
                        <SelectTrigger id="meal-select">
                          <SelectValue placeholder="Seleccionar comida" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableMealTypes.map((mealType) => (
                            <SelectItem key={mealType} value={mealType}>
                              {getMealName(mealType)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="portion-size">Tamaño de porción</Label>
                      <Select 
                        value={portion.toString()} 
                        onValueChange={val => setPortion(parseFloat(val))}
                      >
                        <SelectTrigger id="portion-size">
                          <SelectValue placeholder="Seleccionar porción" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0.5">Media porción (x0.5)</SelectItem>
                          <SelectItem value="1">Porción normal (x1)</SelectItem>
                          <SelectItem value="2">Doble porción (x2)</SelectItem>
                          <SelectItem value="3">Triple porción (x3)</SelectItem>
                          <SelectItem value="4">Cuádruple porción (x4)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="text-sm text-gray-600">
                      <p>Calorías: {Math.round(sanitizedRecipe.calories * portion)} kcal</p>
                      <p>Proteínas: {Math.round(sanitizedRecipe.protein * portion)}g</p>
                    </div>
                    
                    <Button 
                      className="w-full bg-vegana-primary hover:bg-vegana-primary/90" 
                      onClick={handleAddToMenu}
                      disabled={isAdding || !hasActiveSubscription}
                    >
                      {isAdding ? 'Añadiendo...' : 'Añadir'}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
          
          {/* New confirmation message with link to weekly menu */}
          {addSuccess && (
            <div className="text-green-600 text-sm animate-fade-in">
              Receta añadida. <Link to="/menu-semanal" className="underline hover:text-vegana-primary">Ir al menú semanal</Link>
            </div>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

export default RecipeCard;
